/**
 * M9 CH-8b (D-572 item 3): a claimed ship insures at what the player put in, never at its hull value.
 *
 * - **The problem it closes:** the worker insures a fleet ship at hull value × `valueMult` and pays a loss at that
 *   minus the flat rebuild fee (`strategic/fleet/purchase.ts insurancePayout`: (rebuildBase + moduleValueShare ×
 *   modules) × insuredFeeMult, 7,500 with no modules). With `valueMult = valueMultOf(flaws)` (~0.86–0.92 per flaw) a
 *   one-flaw hauler's 49,650 cr kit insured to ~151–162k: claim → insure → lose printed credits on the renewable
 *   off-screen source (the economy direction's "no credit-minting loss payouts").
 * - **The cap:** the insured value is at most `hulks.json insurance.putInShare` × what went in, the kit at the claim
 *   (goods + fuel cells at base price, `kitValueOf`) plus each yard fix's goods at base price (CH-10), and never above
 *   the flawed hull's value (`valueMultOf`). The worker pays insured − the fee, so a loss returns less than
 *   `putInShare` × what went in. `putInShare` is 0.9 (H-5's line): the worker's payout has no further share, so a cap at
 *   1 would return up to put-in − 7,500 (above 0.9 × put-in once put-in passes 75,000, e.g. any hauler after one fix).
 * - **No new save column:** the cap travels as the factor the Career already sends and keeps, `valueMult` (Career
 *   `OwnedShip.valueMult`, the worker's `FleetStore.valueMult`, its commands' `valueMult`, all (0, 1]). At the claim
 *   `claimedValueMult` (CH-7s's `registerClaimedShip`); at each fix `insuredValueMult` from the entry's factor (CH-10's
 *   `setHullFlaws`). By induction the insured value never exceeds `putInShare` × the running total put in.
 * - **The full hull value, never the claim value** (the kit rule, T52): the cap divides by `hullValueOf` (the full
 *   build cost at base price, the mirror of the worker's `hullValue`, which `insurancePayout` multiplies by
 *   `valueMult`), never by `claimValueOf`. The kit (`kitValueOf`) reads the claim cost; dividing by anything but the
 *   worker's own hull value would let the cap slip through `insurancePayout`.
 * - **What does not read it:** scrap (`scrap.ts`, H-5) reads the flaw count × claim value, never `valueMult`; danger
 *   (`combat/resolve.ts lossValueOf`) reads the plain hull; the hangar's "Worth N% of a new hull" reads the flaws
 *   (`valueMultOf`). A bought ship never passes through here (no flaws, `valueMult` 1): byte-identical.
 *
 * Pure; claim and yard time only, never per frame.
 */
import { hulksConfig, type HulksConfig } from './data'
import { goodsValue, hullValueOf, valueMultOf } from './flaws'
import { hulkKit } from './survey'

/** The smallest factor written (the save schemas take (0, 1]). */
const MIN_FACTOR = 1e-6
/** Factors are floored to this many steps per 1 (short in the save, and never above the exact cap). */
const STEPS = 1e6

/** What a claim puts in: the kit's goods and fuel cells at base price (CH-4's `hulkKit`), 0 for an unknown hull. */
export function kitValueOf(hull: string, cfg: HulksConfig = hulksConfig()): number {
  return goodsValue(hulkKit(hull, cfg).goods)
}

/**
 * The insured value factor of a claimed `hull` with the `flaws` bitmask once `putIn` more credits' worth of goods (at
 * base price) went in on top of the factor `prior` it insured at: min(`valueMultOf(flaws)`, `prior` + `putInShare` ×
 * `putIn` ÷ hull value), the second term floored to 1e-6. A claim is `prior` 0 with the kit (`claimedValueMult`); a
 * yard fix is the entry's factor with the fix's goods. An unknown hull (value 0) gives `valueMultOf(flaws)`.
 */
export function insuredValueMult(hull: string, flaws: number, prior: number, putIn: number, cfg: HulksConfig = hulksConfig()): number {
  const worth = valueMultOf(flaws, cfg)
  // The worker mirror (full build cost), never claimValueOf: the worker multiplies its own hullValue (T52).
  const value = hullValueOf(hull)
  if (value <= 0) return worth
  const cap = Math.floor((prior + (cfg.insurance.putInShare * putIn) / value) * STEPS) / STEPS
  return Math.max(MIN_FACTOR, Math.min(worth, cap))
}

/** The insured value factor a claimed `hull` registers with: its kit is all that went in (CH-7s `claimHulk`). */
export function claimedValueMult(hull: string, flaws: number, cfg: HulksConfig = hulksConfig()): number {
  return insuredValueMult(hull, flaws, 0, kitValueOf(hull, cfg), cfg)
}
